import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { problemText, toApiError, useApi, type BookingResponse } from '@/shared/api';
import { earlyDepartureProblemText } from './earlyDeparturePresentation';
import type { useEarlyDepartureCommandIds } from './useEarlyDepartureCommandIds';

/** Eine vorzeitige Abreise ändert Buchung, Belegung und Vertrieb – alle drei laden neu. */
export async function invalidateEarlyDeparture(client: QueryClient) {
  await Promise.all([
    client.invalidateQueries({ queryKey: ['bookings'] }),
    client.invalidateQueries({ queryKey: ['calendar'] }),
    client.invalidateQueries({ queryKey: ['availability-distribution'] }),
  ]);
}

/** „Plan aufheben“: mit eigener Befehls-Id; ein Fehler sagt „aufheben“, nicht „speichern“. */
export function useCancelEarlyDeparturePlan(
  booking: BookingResponse | undefined,
  commandIds: ReturnType<typeof useEarlyDepartureCommandIds>,
) {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!booking?.earlyDeparturePlan) throw new Error('Der Abreiseplan fehlt.');
      return api.bookings.cancelEarlyDeparturePlan(booking.id, {
        commandId: commandIds.get('cancel'),
        expectedConcurrency: booking.earlyDeparturePlan.concurrency,
      });
    },
    onSuccess: async () => {
      commandIds.reset('cancel', 'plan');
      await invalidateEarlyDeparture(client);
      toast.success('Geplante Abreise aufgehoben');
    },
    onError: async (error) => {
      const problem = await toApiError(error);
      toast.error(
        problem.status === 409
          ? earlyDepartureProblemText(problem)
          : problemText(problem, 'Die geplante Abreise konnte nicht aufgehoben werden.'),
      );
    },
  });
}
